/**
 * ContentCreationWizard - centered modal dialog wizard for creating new content
 *
 * Two entry points:
 * A) "I have keywords" - Quick Create flow
 * B) "Find keywords" - Research -> Clusters -> Template -> Create
 *
 * Both paths end with: Select Template -> Create Blog Posts
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useBrandBible } from '@/hooks/useBrandBible';
import {
  ArrowLeft,
  ChevronRight,
  AlertTriangle,
  Eye,
  Search,
  Layers,
  Target,
  TrendingUp,
  Trash2,
  RefreshCw,
  Plus,
  Sparkles,
  Zap,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { contentPlannerService } from '@/services/contentPlannerService';
import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import { useAdsViewStore } from '@/stores/useAdsViewStore';
import { useBlogTemplate, useBlogTemplates, useRecommendedTemplates } from '@/hooks/useBlogTemplates';
import { SuggestedTemplateBanner } from './shared/SuggestedTemplateBanner';
import { ApprovedTemplatesPanel } from '@/components/blog-templates/ApprovedTemplatesPanel';
import { resolveApprovedTemplates } from '@/constants/approvedBlogTemplates';
import { useCreateContent, useBridgeStatus } from '@/hooks/useBlogCreationAPI';
import { CreateContentData } from '@/components/BlogCreationCommandCenter/types';
import type { AutoScheduleSettings } from '@/components/BlogCreationCommandCenter/panels/TemplateSelectionModal';
import { TemplateSelectionModal } from '@/components/BlogCreationCommandCenter/panels/TemplateSelectionModal';
import { BatchScheduleDuplicateDialog } from '@/components/BlogCreationCommandCenter/panels/BatchScheduleDuplicateDialog';
import {
  duplicateSkipContentIds,
  hasDuplicateSkips,
} from '@/components/BlogCreationCommandCenter/panels/batchScheduleDuplicateUtils';
import { blogCreationService } from '@/services/blogCreationService';
import type { GenerateAndScheduleResponse } from '@/services/blogCreationService';
import {
  useTopicalMaps,
  useCreateTopicalMap,
  useDeleteTopicalMap,
  useKeywordSuggestions,
} from '@/hooks/useContentPlannerAPI';
import { useTenantStore } from '@/stores/useTenantStore';
import { useAutoPublishStore } from '@/stores/useAutoPublishStore';
import {
  TopicalMapStatus,
  REGION_OPTIONS,
} from '@/types/contentPlanner';
import type { KeywordCheckResponse } from '@/types/contentPlanner';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Progress, Tag, Popconfirm, Typography, AutoComplete } from 'antd';
import { KeywordInputForm } from '@/components/content-planner/KeywordInputForm';
import { ContentPlannerStats } from '@/components/content-planner/ContentPlannerStats';
import { useWPHealthGate } from '@/hooks/useWPHealthGate';
import { WPHealthWarningDialog } from '@/components/content-planner/WPHealthWarningDialog';
import { useResizableModal } from '@/hooks/useResizableModal';
import { ModalResizeHandles } from '@/components/ui/ModalResizeHandles';
import { ResearchSessionsDrawer } from '@/components/content-planner/ResearchSessionsDrawer';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { universalBlogEditorPath } from '@/utils/editorRoutes';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PenWritingLoader } from '@/components/content-planner/PenWritingLoader';

const { Text } = Typography;

// Status display config for keyword research
const STATUS_CONFIG: Record<TopicalMapStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'default' },
  fetching_keywords: { label: 'Fetching Keywords', color: 'processing' },
  classifying: { label: 'Classifying', color: 'processing' },
  clustering: { label: 'Clustering', color: 'processing' },
  organizing: { label: 'Organizing', color: 'processing' },
  completed: { label: 'Completed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

type EntryPoint = 'choose' | 'have-keywords' | 'find-keywords';

// New Article always opens on the mode chooser. Quick Create v2 (#1309) trims it
// to two options (Quick Create + Bulk Generate); flag off keeps the original four.
// Deep links (?step=keywords, or a template/keyword/map param) still bypass straight
// into Quick Create — see the entryPoint initializer below.
const DEFAULT_ENTRY_POINT: EntryPoint = 'choose';

export interface ContentCreationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When opened from the "New Article" dropdown (#1340), which mode to jump
   * straight into — skipping the chooser. 'quick-create' → the have-keywords
   * flow; 'bulk' → the bulk scheduler. Omitted = the normal chooser.
   */
  initialMode?: 'quick-create' | 'bulk' | null;
}

export const ContentCreationWizard: React.FC<ContentCreationWizardProps> = ({
  open,
  onOpenChange,
  initialMode = null,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTenant } = useTenantStore();
  const { enabled: autoPublishEnabled, setEnabled: setAutoPublishEnabled } = useAutoPublishStore();

  // Bridge publishing
  const { data: bridgeData } = useBridgeStatus();
  const bridgeEnabled = bridgeData?.bridge_enabled ?? false;
  const [useBridge, setUseBridge] = useState<boolean>(false);

  // URL params
  const templateId = searchParams.get('template');
  const keywordParam = searchParams.get('keyword');
  const mapParam = searchParams.get('map');
  const stepParam = searchParams.get('step');

  // WP post type state
  const [wpPostType, setWpPostType] = useState<'post' | 'page'>('post');

  // Bulk Generate modal state
  const [bulkGenerateOpen, setBulkGenerateOpen] = useState(false);
  const [lastBulkScheduleSettings, setLastBulkScheduleSettings] = useState<AutoScheduleSettings | null>(null);
  const [duplicateScheduleResult, setDuplicateScheduleResult] = useState<GenerateAndScheduleResponse | null>(null);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);

  // Entry point state - derive initial value from URL to prevent flicker
  const [entryPoint, setEntryPoint] = useState<EntryPoint>(() => {
    if (stepParam === 'research') return 'find-keywords';
    if (stepParam === 'keywords') return 'have-keywords';
    if (templateId || keywordParam || mapParam) return 'have-keywords';
    if (initialMode === 'quick-create') return 'have-keywords';
    return DEFAULT_ENTRY_POINT;
  });

  // Quick Create v2 (#1309): Bulk Generate is live for staff, "coming soon" for
  // clients. Same staff predicate as the tickets board — and staff can preview
  // the client card via the global Internal/Client lens (#1334).
  const userProfile = useWorkspaceStore((s) => s.userProfile);
  const adsViewMode = useAdsViewStore((s) => s.viewMode);
  const bulkComingSoon =
    !canAccessInternalAdsTools(userProfile) || adsViewMode === 'client';

  // Opened from the New Article dropdown (#1340) straight into a mode, skipping
  // the chooser. 'quick-create' jumps to the have-keywords flow; 'bulk' suppresses
  // the chooser (open gate above) and opens the bulk scheduler, releasing the
  // parent's open state so it resets cleanly. Bulk is client-gated.
  useEffect(() => {
    if (!open) return;
    if (initialMode === 'quick-create') {
      setEntryPoint('have-keywords');
    } else if (initialMode === 'bulk' && !bulkComingSoon) {
      setBulkGenerateOpen(true);
      onOpenChange(false);
    }
  }, [open, initialMode, bulkComingSoon, onOpenChange]);

  // Template state
  const {
    data: selectedTemplate,
    isLoading: isLoadingTemplate,
    isError: isTemplateError,
  } = useBlogTemplate(templateId);
  const { data: templatesData } = useBlogTemplates();
  const templates = templatesData?.templates || [];
  // R5 ruled source for Recommended: list_recommended endpoint. While the
  // query is loading/errors, recommendedIds stays undefined and the chooser's
  // client-side heuristic is the ruled fallback (endpoint resilience).
  const { data: recommendedTemplates } = useRecommendedTemplates();
  const recommendedIds = useMemo(
    () => recommendedTemplates?.map((t) => t.id),
    [recommendedTemplates],
  );
  const [localTemplateId, setLocalTemplateId] = useState<string | null>(templateId);

  const isTemplateNotFound = isTemplateError && templateId;

  const handleTemplateChange = useCallback((newTemplateId: string | null) => {
    setLocalTemplateId(newTemplateId);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (newTemplateId) {
        newParams.set('template', newTemplateId);
      } else {
        newParams.delete('template');
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const navigateToTemplates = useCallback(() => {
    navigate('/content/templates');
  }, [navigate]);

  // Quick Create state
  const [keywordsText, setKeywordsText] = useState('');
  const [targetWordCount, setTargetWordCount] = useState(1800);
  const [blogTitle, setBlogTitle] = useState('');
  const [errors, setErrors] = useState<{ keywords?: string; seedKeyword?: string; topic?: string; title?: string }>({});
  // Quick Create v2 (#row3): optional publish date chosen at create time (datetime-local).
  const [scheduledDate, setScheduledDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Distinct phase so the overlay shows "Checking for existing content..." while
  // the (sometimes slow) duplicate lookup runs, instead of implying it's already
  // creating — otherwise the wait reads as "nothing happened".
  const [isCheckingDup, setIsCheckingDup] = useState(false);

  // Keyword Research state
  const [seedKeyword, setSeedKeyword] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('US');
  const [isCreatingMap, setIsCreatingMap] = useState(false);

  // Researched keywords panel state
  const [expandedMapId, setExpandedMapId] = useState<string | null>(null);
  const [showResearchDrawer, setShowResearchDrawer] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());

  // API hooks
  const createContentMutation = useCreateContent();
  const { data: mapsData, isLoading: mapsLoading, isPending: mapsPending, refetch: refetchMaps } = useTopicalMaps(currentTenant);
  const createMapMutation = useCreateTopicalMap();
  const deleteMapMutation = useDeleteTopicalMap();
  const keywordSuggestionsMutation = useKeywordSuggestions();

  // Brand Bible for service suggestions
  const { data: brandBible } = useBrandBible(currentTenant);

  // LLM-generated keyword suggestions state
  const [llmSuggestions, setLlmSuggestions] = useState<string[]>([]);
  const [isStreamingSuggestions, setIsStreamingSuggestions] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const streamAbortControllerRef = useRef<AbortController | null>(null);

  // Title suggestions state (SERP-powered)
  const [isSuggestingTitles, setIsSuggestingTitles] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState<string[]>([]);
  const [competitorCount, setCompetitorCount] = useState(0);

  // Rotating progress messages for title suggest
  const [titleSuggestMsgIndex, setTitleSuggestMsgIndex] = useState(0);
  const TITLE_SUGGEST_MESSAGES = useMemo(() => [
    'Analyzing competitor titles from search results...',
    'Researching top-ranking pages for your keyword...',
    'Generating SEO-optimized title options with AI...',
    'Evaluating title patterns and click-through potential...',
    'Finalizing suggestions — almost done...',
  ], []);

  useEffect(() => {
    if (!isSuggestingTitles) { setTitleSuggestMsgIndex(0); return; }
    const interval = setInterval(() => {
      setTitleSuggestMsgIndex(prev => Math.min(prev + 1, TITLE_SUGGEST_MESSAGES.length - 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [isSuggestingTitles, TITLE_SUGGEST_MESSAGES]);

  // Selected topic from AI topic suggestions
  const [selectedTopic, setSelectedTopic] = useState('');

  // Keyword dedup confirmation state
  const [dedupConfirmData, setDedupConfirmData] = useState<KeywordCheckResponse | null>(null);
  const [showDedupConfirm, setShowDedupConfirm] = useState(false);

  // Drag-resizable + fullscreen modal sizing, persisted across opens.
  const modalSize = useResizableModal({
    storageKey: 'content-creation-wizard-size-v1',
    defaultWidth: 768, // matches the previous max-w-3xl
    defaultHeight: 640,
    minWidth: 480,
    minHeight: 380,
  });

  // Handler to fetch LLM suggestions with live status updates
  const handleGetSuggestions = useCallback(() => {
    if (!brandBible || isStreamingSuggestions) return;

    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
    }

    setIsStreamingSuggestions(true);
    setStreamingStatus('Starting...');

    const controller = contentPlannerService.streamKeywordSuggestions(
      {
        services_offered: brandBible.services_offered || [],
        customer_pain_points: brandBible.customer_pain_points || '',
        marketing_focus: brandBible.marketing_focus || '',
        ideal_client: brandBible.ideal_client || '',
        business_name: brandBible.business_name || currentTenant || '',
        exclude_existing: true,
      },
      {
        onStatus: (message) => {
          setStreamingStatus(message);
        },
        onKeywords: (keywords, count, excludedCount) => {
          setLlmSuggestions(keywords);
          setStreamingStatus(`Found ${count} keywords${excludedCount > 0 ? ` (${excludedCount} excluded)` : ''}`);
          if (keywords.length > 0 && !seedKeyword.trim()) {
            setSeedKeyword(keywords[0]);
          }
        },
        onError: (message) => {
          console.error('LLM suggestions stream error:', message);
          setStreamingStatus(`Error: ${message}`);
        },
        onComplete: () => {
          setIsStreamingSuggestions(false);
        },
      }
    );

    streamAbortControllerRef.current = controller;
  }, [brandBible, currentTenant, isStreamingSuggestions]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Handler to fetch SERP-powered title suggestions
  const handleSuggestTitles = useCallback(async () => {
    const keywords = parseKeywords(keywordsText);
    if (keywords.length === 0 || isSuggestingTitles) return;

    const primaryKeyword = keywords[0];
    setIsSuggestingTitles(true);
    setTitleSuggestions([]);

    try {
      const response = await contentPlannerService.suggestTitles(
        primaryKeyword,
        selectedTopic || undefined,
      );
      setTitleSuggestions(response.suggestions);
      setCompetitorCount(response.competitor_count);

      if (!blogTitle && response.suggestions.length > 0) {
        setBlogTitle(response.suggestions[0]);
      }
    } catch (error) {
      console.error('Failed to suggest titles:', error);
      toast.error('Failed to generate title suggestions');
    } finally {
      setIsSuggestingTitles(false);
    }
  }, [keywordsText, isSuggestingTitles, blogTitle, selectedTopic]);

  // Create autocomplete options from Brand Bible services + LLM suggestions
  const allSuggestionOptions = useMemo(() => {
    const options: { value: string; label: React.ReactNode }[] = [];

    if (brandBible?.services_offered) {
      brandBible.services_offered.forEach((service: string) => {
        options.push({
          value: service,
          label: (
            <span className="flex items-center justify-between w-full">
              <span>{service}</span>
              <span className="text-xs text-teal ml-2">service</span>
            </span>
          ),
        });
      });
    }

    llmSuggestions.forEach((suggestion) => {
      const exists = options.some(
        (opt) => opt.value.toLowerCase() === suggestion.toLowerCase()
      );
      if (!exists) {
        options.push({
          value: suggestion,
          label: (
            <span className="flex items-center justify-between w-full">
              <span>{suggestion}</span>
              <span className="text-xs text-coral ml-2">suggested</span>
            </span>
          ),
        });
      }
    });

    return options;
  }, [brandBible, llmSuggestions]);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!seedKeyword.trim()) return allSuggestionOptions;
    const search = seedKeyword.toLowerCase();
    return allSuggestionOptions.filter((opt) =>
      opt.value.toLowerCase().includes(search)
    );
  }, [seedKeyword, allSuggestionOptions]);

  const maps = mapsData?.maps || [];
  const completedMaps = maps.filter(m => m.status === 'completed');
  const inProgressMaps = maps.filter(m => !['completed', 'failed'].includes(m.status));

  // Parse keywords from textarea
  const parseKeywords = (text: string): string[] => {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0 && line.length <= 255);
  };

  const keywordCount = parseKeywords(keywordsText).length;

  // Get current keywords as a set for quick lookup
  const currentKeywordsSet = useMemo(() => {
    return new Set(parseKeywords(keywordsText).map(k => k.toLowerCase()));
  }, [keywordsText]);

  // Add a keyword to the textarea (if not already present)
  const addKeywordToTextarea = useCallback((keyword: string) => {
    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) return;

    setKeywordsText(prev => {
      const existingKeywords = parseKeywords(prev);
      const isDuplicate = existingKeywords.some(
        k => k.toLowerCase() === trimmedKeyword.toLowerCase()
      );
      if (isDuplicate) return prev;
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n${trimmedKeyword}` : trimmedKeyword;
    });
  }, []);

  // Add multiple keywords at once
  const addKeywordsToTextarea = useCallback((keywords: string[]) => {
    setKeywordsText(prev => {
      const existingSet = new Set(parseKeywords(prev).map(k => k.toLowerCase()));
      const newKeywords = keywords.filter(k => {
        const trimmed = k.trim().toLowerCase();
        return trimmed && !existingSet.has(trimmed);
      });
      if (newKeywords.length === 0) return prev;
      const trimmed = prev.trim();
      const toAdd = newKeywords.map(k => k.trim()).join('\n');
      return trimmed ? `${trimmed}\n${toAdd}` : toAdd;
    });
  }, []);

  // Populate keywords text from URL param
  useEffect(() => {
    if (keywordParam) {
      setKeywordsText(keywordParam);
    }
  }, [keywordParam]);

  // Handle step param changes for navigation
  useEffect(() => {
    if (stepParam === 'keywords') {
      setEntryPoint('have-keywords');
    } else if (stepParam === 'research') {
      setEntryPoint('find-keywords');
    }
  }, [stepParam]);

  // Update template selection when URL param changes
  useEffect(() => {
    if (templateId) {
      setLocalTemplateId(templateId);
    }
  }, [templateId]);

  // Expand map automatically when navigating with map param
  useEffect(() => {
    if (mapParam && completedMaps.some(m => m.map_id === mapParam)) {
      setExpandedMapId(mapParam);
    }
  }, [mapParam, completedMaps]);

  // Update target word count when template loads
  useEffect(() => {
    if (selectedTemplate?.default_config?.targetWordCount) {
      setTargetWordCount(selectedTemplate.default_config.targetWordCount);
    }
  }, [selectedTemplate]);

  // Quick Create constrains the effective selection to the four approved
  // UUIDs so the picker's DISPLAY and the create PAYLOAD agree (M1).
  const effectiveTemplateId = useMemo(
    () => {
      return resolveApprovedTemplates(templatesData?.templates ?? [], {
        preselectedTemplateId: localTemplateId ?? undefined,
      }).preselectedTemplateId;
    },
    [templatesData, localTemplateId],
  );

  // Never fall back to an off-allowlist URL template — the approved picker owns
  // selection.
  const currentTemplate = templates.find(t => t.id === effectiveTemplateId);

  // The shared banner supplies the advisory, high-confidence suggestion while
  // the Phase-3 chooser owns selection, recency, and recommendations.
  const primaryKeyword = useMemo(() => parseKeywords(keywordsText)[0] || '', [keywordsText]);
  // =============================================================================
  // Quick Create Handlers
  // =============================================================================

  const validateQuickCreate = (): boolean => {
    const newErrors: { keywords?: string; topic?: string; title?: string } = {};
    const keywords = parseKeywords(keywordsText);

    if (keywords.length === 0) {
      newErrors.keywords = 'Please enter at least one keyword';
    } else if (keywords.length > 50) {
      newErrors.keywords = 'Maximum 50 keywords allowed at once';
    }

    // Quick Create v2 (row 3): Topic and Title are required.
    if (!selectedTopic?.trim()) {
      newErrors.topic = 'Please choose a topic';
    }
    if (!blogTitle.trim()) {
      newErrors.title = 'Please enter a title';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const executeCreateBlogs = async () => {
    const keywords = parseKeywords(keywordsText);
    setIsSubmitting(true);
    setShowDedupConfirm(false);
    setDedupConfirmData(null);

    try {
      const [primaryKeyword, ...secondaryKeywords] = keywords;

      const data: CreateContentData = {
        keyword: primaryKeyword,
        secondary_keywords: secondaryKeywords.length > 0 ? secondaryKeywords : undefined,
        target_word_count: targetWordCount,
        title: blogTitle.trim() || undefined,
        topic_to_explore: selectedTopic?.trim() || undefined,
        template_id: currentTemplate?.id,
        content_template: currentTemplate?.content_template,
        publish_immediately: autoPublishEnabled,
        use_bridge: bridgeEnabled ? useBridge : undefined,
        wp_post_type: wpPostType,
      };

      const created = await createContentMutation.mutateAsync(data);
      // Quick Create v2 (#row3): if the user picked a publish date, schedule the
      // just-created draft (best-effort — a schedule failure must not lose the
      // created article; the backend endpoint already validates a future time).
      if (scheduledDate && created?.id) {
        try {
          await blogCreationService.schedule(created.id, new Date(scheduledDate).toISOString());
        } catch (schedErr) {
          console.error('Article created, but scheduling failed:', schedErr);
        }
      }
      // Close the drawer so the user lands on the planner table (key UX fix —
      // mirrors the bulk path's close behavior). Then navigate, handing off the
      // new content id via the shared `new` param so the table can scroll/highlight it.
      onOpenChange(false);
      navigate(`/content/planner?view=table${created?.id ? `&new=${created.id}` : ''}`);
    } catch (error) {
      // The create mutation's onError already surfaces a toast — don't double-toast here.
      console.error('Failed to create content:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { triggerWithHealthCheck, isChecking: isCheckingWPHealth, warningProps: wpHealthWarningProps } = useWPHealthGate(executeCreateBlogs);

  const handleBulkGenerateSubmit = async (settings: AutoScheduleSettings) => {
    setIsSubmitting(true);
    setLastBulkScheduleSettings(settings);
    try {
      const result = await blogCreationService.generateAndSchedule(
        settings.clusterIds.length || settings.numPosts,
        settings.numDays,
        3,
        '09:00',
        settings.templateId ?? undefined,
        settings.modelOverride ?? undefined,
        settings.imageModelOverride ?? undefined,
        settings.autoPublish,
        settings.clusterIds.length ? settings.clusterIds : undefined,
        settings.assistanceLevel,
        settings.exportToGdocs,
        settings.wordpressPublishMode,
      );
      if (result.success) {
        if (hasDuplicateSkips(result)) {
          setDuplicateScheduleResult(result);
        }
        if (result.scheduled_posts.length > 0) {
          setBulkGenerateOpen(false);
          toast.success(result.message);
          navigate('/content/planner?view=table');
        } else {
          toast.warning('No new posts were started because the selected keywords already have active drafts.');
        }
      }
    } catch (error: unknown) {
      const axiosErr = error as AxiosError<{ detail?: string }>;
      toast.error(axiosErr?.response?.data?.detail || 'Failed to schedule posts. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDuplicateContent = (contentId: string) => {
    setDuplicateScheduleResult(null);
    setBulkGenerateOpen(false);
    navigate(universalBlogEditorPath(contentId));
  };

  const handleDeleteDuplicatesAndRetry = async () => {
    if (!lastBulkScheduleSettings || !duplicateScheduleResult) return;
    const duplicateIds = duplicateSkipContentIds(duplicateScheduleResult);
    if (duplicateIds.length === 0) return;

    setDuplicateActionLoading(true);
    try {
      await blogCreationService.batchDelete(duplicateIds);
      toast.success(`Deleted ${duplicateIds.length} duplicate draft${duplicateIds.length === 1 ? '' : 's'}. Restarting generation...`);
      setDuplicateScheduleResult(null);
      await handleBulkGenerateSubmit(lastBulkScheduleSettings);
    } catch (error: unknown) {
      const axiosErr = error as AxiosError<{ detail?: string }>;
      toast.error(axiosErr?.response?.data?.detail || 'Failed to delete duplicate drafts.');
    } finally {
      setDuplicateActionLoading(false);
    }
  };

  const handleCreateBlogs = async () => {
    if (!validateQuickCreate()) return;

    const keywords = parseKeywords(keywordsText);
    const primaryKeyword = keywords[0];

    // Show the overlay immediately on click (in the "checking" phase) so the
    // button never feels dead while the duplicate lookup is in flight.
    setIsSubmitting(true);
    setIsCheckingDup(true);

    try {
      const dedupResult = await contentPlannerService.checkKeywordDuplicate(primaryKeyword);
      if (dedupResult.exists) {
        setIsCheckingDup(false);
        setIsSubmitting(false);
        setDedupConfirmData(dedupResult);
        setShowDedupConfirm(true);
        return;
      }
    } catch {
      console.warn('Dedup check failed, proceeding with creation');
    } finally {
      setIsCheckingDup(false);
    }

    await triggerWithHealthCheck();
  };

  // =============================================================================
  // Keyword Research Handlers
  // =============================================================================

  const handleCreateMap = () => {
    if (!seedKeyword.trim()) {
      setErrors({ seedKeyword: 'Please enter a seed keyword' });
      return;
    }

    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    setIsCreatingMap(true);
    setErrors({});

    const controller = contentPlannerService.createMapWithStreaming(
      {
        seed_keyword: seedKeyword.trim(),
        region: selectedRegion,
        language: 'en',
      },
      {
        onCreated: (mapId) => {
          console.log('Map created:', mapId);
          toast.success('Keyword research started!');
        },
        onProgress: (status, progress, stage) => {
          console.log(`Progress: ${progress}% - ${stage}`);
        },
        onComplete: (mapId, status, fromCache) => {
          console.log('Map complete:', mapId, status, fromCache ? '(from cache)' : '');
          setIsCreatingMap(false);
          setSeedKeyword('');
          // Auto-expand the just-completed map so the result is visible instead of
          // leaving the user staring at the same research step (dead-end feedback fix).
          setExpandedMapId(mapId);
          if (fromCache) {
            toast.success('Found existing research for this keyword!');
          } else {
            toast.success('Keyword research complete!');
          }
          refetchMaps();
        },
        onError: (message) => {
          console.error('Map creation error:', message);
          setIsCreatingMap(false);
          toast.error(`Failed: ${message}`);
        },
      }
    );
  };

  const handleViewMap = (mapId: string) => {
    navigate(`/content/planner/research/${mapId}`);
  };

  const handleDeleteMap = async (mapId: string) => {
    if (!currentTenant) return;

    try {
      await deleteMapMutation.mutateAsync({
        tenantId: currentTenant,
        mapId,
      });
      toast.success('Research session deleted');
      // Belt-and-suspenders: explicitly refetch so the maps list can't look stale
      // even if the hook's own invalidation hasn't propagated yet.
      refetchMaps();
    } catch (error) {
      console.error('Failed to delete map:', error);
      toast.error('Failed to delete research session');
    }
  };

  const handleRetryMap = async (map: typeof maps[0]) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    try {
      await deleteMapMutation.mutateAsync({
        tenantId: currentTenant,
        mapId: map.map_id,
      });

      const newMap = await createMapMutation.mutateAsync({
        tenantId: currentTenant,
        data: {
          seed_keyword: map.seed_keyword,
          region: map.region || 'US',
          language: map.language || 'en',
        },
      });

      toast.success('Research restarted!');
      navigate(`/content/planner/research/${newMap.map_id}`);
    } catch (error) {
      console.error('Failed to retry research:', error);
      toast.error('Failed to restart research. Please try again.');
    }
  };

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <>
      <Dialog
        open={open && initialMode !== 'bulk'}
        onOpenChange={(isOpen) => {
          onOpenChange(isOpen);
          if (!isOpen) {
            setEntryPoint(DEFAULT_ENTRY_POINT);
            setWpPostType('post');
            // Reset transient dedup-warning state on close. Without this the flag
            // stayed true after an attempt, so the warning only surfaced on the
            // NEXT open (the reported "shows after close+reopen" bug).
            setShowDedupConfirm(false);
            setDedupConfirmData(null);
          }
        }}
      >
        <DialogContent
          className="p-0 gap-0 flex flex-col overflow-hidden"
          style={modalSize.contentStyle}
        >
          {/* Fullscreen / restore toggle — sits just left of the built-in close X */}
          <button
            type="button"
            onClick={modalSize.toggleFullscreen}
            className="absolute right-12 top-4 z-[70] rounded-sm p-0.5 text-muted-foreground opacity-70 transition-opacity hover:text-coral hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-coral/30"
            aria-label={modalSize.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            title={modalSize.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {modalSize.isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>

          {/* Creation Loading Overlay (inside dialog) */}
          {isSubmitting && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-card/80 backdrop-blur-sm rounded-lg">
              <PenWritingLoader size={132} />
              <p className="text-lg font-semibold text-foreground mt-4">
                {isCheckingDup ? 'Checking for existing content...' : 'Setting up your article...'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {isCheckingDup ? 'Making sure this keyword is not already covered' : 'This usually takes a few seconds'}
              </p>
            </div>
          )}

          <DialogHeader className="px-6 py-4 border-b sticky top-0 bg-background z-10">
            <DialogTitle>
              {entryPoint === 'choose' && 'How would you like to create?'}
              {entryPoint === 'have-keywords' && `Create ${wpPostType === 'page' ? 'Page' : 'Article'}`}
              {entryPoint === 'find-keywords' && 'Research & Create'}
            </DialogTitle>
          </DialogHeader>
          {/* mr-2 insets the vertical scrollbar from the box's right edge so it
              doesn't sit over (and steal pointer events from) the right/corner
              resize handles when the content is tall. The 8px strip is white-on-
              white, so it's invisible. */}
          <div className="flex-1 overflow-y-auto px-6 py-4 mr-2">
        <>
          {/* Invalid/Deleted Template Warning Banner */}
          {isTemplateNotFound && (
            <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <span>The template you selected is no longer available. Using default settings.</span>
              </div>
              <Button variant="outline" size="sm" onClick={navigateToTemplates}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Browse Templates
              </Button>
            </div>
          )}

          {/* Mode selection */}
          {entryPoint === 'choose' && (
            <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              {/* Quick Create */}
              <Card
                className="p-5 cursor-pointer hover:border-teal hover:shadow-md transition-all group border-2 border-transparent"
                onClick={() => setEntryPoint('have-keywords')}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-teal-light flex items-center justify-center group-hover:bg-teal/20 transition-colors flex-shrink-0">
                    <Zap className="h-6 w-6 text-teal-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                      Quick Create
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-teal transition-colors" />
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Enter a keyword, pick a template, and let AI write the article
                    </p>
                  </div>
                </div>
              </Card>

              {/* Bulk Generate — live for staff; "coming soon" for clients in v2 (#1309) */}
              <Card
                className={cn(
                  "p-5 transition-all group border-2 border-transparent",
                  bulkComingSoon
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:border-teal hover:shadow-md",
                )}
                onClick={bulkComingSoon ? undefined : () => { onOpenChange(false); setBulkGenerateOpen(true); }}
                aria-disabled={bulkComingSoon}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-teal-light flex items-center justify-center group-hover:bg-teal/20 transition-colors flex-shrink-0">
                    <Layers className="h-6 w-6 text-teal-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                      Bulk Generate
                      {bulkComingSoon ? (
                        <Badge variant="secondary" className="text-[10px]">Coming soon</Badge>
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-teal transition-colors" />
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {bulkComingSoon
                        ? 'Generate multiple articles at once — coming soon for your workspace'
                        : 'Paste a list of keywords and generate multiple articles at once'}
                    </p>
                  </div>
                </div>
              </Card>

            </div>
            </div>
          )}

          {/* Option A: Have Keywords Flow */}
          {entryPoint === 'have-keywords' && (
            <div className="space-y-6">
              {/* Step 1: Keywords */}
              <KeywordInputForm
                templateContentType={currentTemplate?.default_config?.contentType ?? null}
                keywordsText={keywordsText}
                onKeywordsChange={setKeywordsText}
                keywordCount={keywordCount}
                keywordsError={errors.keywords}
                requireTopicTitle={true}
                topicError={errors.topic}
                titleError={errors.title}
                tenantId={currentTenant}
                brandBible={brandBible || null}
                completedMaps={completedMaps}
                mapsLoading={mapsLoading}
                mapsPending={mapsPending}
                expandedMapId={expandedMapId}
                onToggleMap={(mapId) =>
                  setExpandedMapId(expandedMapId === mapId ? null : mapId)
                }
                onShowMoreResearch={() => setShowResearchDrawer(true)}
                totalMapsCount={completedMaps.length}
                addKeywordToTextarea={addKeywordToTextarea}
                addKeywordsToTextarea={addKeywordsToTextarea}
                currentKeywordsSet={currentKeywordsSet}
                parseKeywords={parseKeywords}
                blogTitle={blogTitle}
                onBlogTitleChange={(val) => {
                  setBlogTitle(val);
                  setTitleSuggestions([]);
                }}
                selectedTopic={selectedTopic}
                onTopicSelect={setSelectedTopic}
                isSuggestingTitles={isSuggestingTitles}
                titleSuggestions={titleSuggestions}
                competitorCount={competitorCount}
                titleSuggestMessage={TITLE_SUGGEST_MESSAGES[titleSuggestMsgIndex]}
                onSuggestTitles={handleSuggestTitles}
                onSelectTitleSuggestion={(suggestion) => {
                  setBlogTitle(suggestion);
                  setTitleSuggestions([]);
                }}
                isSubmitting={isSubmitting}
              />

              {/* Step 2: Template Selection */}
              <Card id="template-selection" className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <h3 className="font-semibold">Choose Template</h3>
                  <span className="text-sm text-muted-foreground">(optional)</span>
                </div>

                <SuggestedTemplateBanner
                  keyword={primaryKeyword}
                  title={blogTitle || undefined}
                  selectedTemplateId={localTemplateId}
                  onSelect={(id) => handleTemplateChange(id)}
                  className="mb-3"
                />
                {/* Direction B: the approved-four UUID-only rows picker. It
                   self-fetches via useBlogTemplates (react-query dedupes with
                   the wizard's own fetch) and stays controlled by the wizard.
                   onUse is omitted (M2): Step 3 Create is the single commit. */}
                <ApprovedTemplatesPanel
                  selectedId={effectiveTemplateId ?? undefined}
                  onSelect={(t) => handleTemplateChange(t.id)}
                />
              </Card>

              {/* Step 3: Create */}
              <ContentPlannerStats
                keywordCount={keywordCount}
                currentTemplateName={currentTemplate?.name}
                targetWordCount={targetWordCount}
                onWordCountChange={setTargetWordCount}
                hideWordCount={true}
                autoPublishEnabled={autoPublishEnabled}
                onAutoPublishChange={setAutoPublishEnabled}
                bridgeEnabled={bridgeEnabled}
                useBridge={useBridge}
                onUseBridgeToggle={() => setUseBridge(prev => !prev)}
                showSchedule={true}
                scheduledDate={scheduledDate}
                onScheduledDateChange={setScheduledDate}
                isSubmitting={isSubmitting}
                onCreateBlogs={handleCreateBlogs}
              />

              {/* Keyword-dedup + WP-health warning dialogs are rendered as
                  top-level siblings BELOW (outside <Dialog>) so their portals
                  overlay correctly and appear on the first generate click. */}

              {/* Research Sessions Drawer */}
              <ResearchSessionsDrawer
                open={showResearchDrawer}
                onOpenChange={setShowResearchDrawer}
                allMaps={maps}
                tenantId={currentTenant}
                onAddKeyword={addKeywordToTextarea}
                onAddKeywords={addKeywordsToTextarea}
                currentKeywordsSet={currentKeywordsSet}
                refetchMaps={refetchMaps}
                brandBible={brandBible || null}
              />
            </div>
          )}

          {/* Option B: Find Keywords Flow */}
          {entryPoint === 'find-keywords' && (
            <div className="space-y-6">
              {/* New Research Card */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="h-5 w-5 text-coral" />
                  <h3 className="font-semibold">Start Keyword Research</h3>
                </div>

                <div className="space-y-3">
                  {/* Seed keyword input row */}
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label htmlFor="seedKeyword" className="mb-2 block">
                        Seed Keyword
                      </Label>
                      <AutoComplete
                        id="seedKeyword"
                        placeholder="Enter a seed keyword (e.g., 'your main service')"
                        value={seedKeyword}
                        onChange={(value) => setSeedKeyword(value)}
                        onSelect={(value) => setSeedKeyword(value)}
                        options={filteredOptions}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateMap()}
                        className="w-full"
                        style={{ width: '100%' }}
                        status={errors.seedKeyword ? 'error' : undefined}
                      />
                      {errors.seedKeyword && (
                        <p className="text-sm text-destructive mt-1">{errors.seedKeyword}</p>
                      )}
                    </div>
                    {brandBible && (
                      <Button
                        variant="outline"
                        size="default"
                        onClick={handleGetSuggestions}
                        disabled={isStreamingSuggestions}
                        className={`${isStreamingSuggestions ? "" : "animate-ai-suggest"} text-teal border-teal/30 hover:bg-teal-light h-[32px]`}
                        title="Get keyword suggestions based on your Brand Bible"
                      >
                        {isStreamingSuggestions ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-1.5" />
                            {llmSuggestions.length > 0 ? 'Refresh' : 'AI Suggest'}
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Region and submit row */}
                  <div className="flex gap-3 items-end">
                    <div className="w-44">
                      <Label className="mb-2 block">Region</Label>
                      <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REGION_OPTIONS.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              <span className="flex items-center gap-2">
                                <span>{r.flag}</span>
                                <span>{r.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleCreateMap}
                      disabled={isCreatingMap || !seedKeyword.trim() || !currentTenant}
                      className="bg-coral hover:bg-coral-hover text-white"
                    >
                      {isCreatingMap ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Find Keywords
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Status panel */}
                {isStreamingSuggestions && (
                  <div className="mt-3 p-3 bg-teal-light border border-teal/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-teal animate-pulse" />
                      <span className="text-sm font-medium text-teal-dark">{streamingStatus}</span>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground mt-3">
                  {!isStreamingSuggestions && allSuggestionOptions.length > 0 && (
                    <span className="text-teal-dark">
                      {allSuggestionOptions.length} suggestions available.{' '}
                    </span>
                  )}
                  We'll fetch related keywords from Ahrefs and group them into topic clusters.
                </p>
              </Card>

              {/* In Progress */}
              {inProgressMaps.length > 0 && (
                <Card className="p-6 border-teal/30 bg-teal-light/50">
                  <div className="flex items-center gap-2 mb-4">
                    <RefreshCw className="h-5 w-5 text-teal animate-spin" />
                    <h3 className="font-semibold">Processing</h3>
                  </div>

                  <div className="space-y-3">
                    {inProgressMaps.map((map) => {
                      const config = STATUS_CONFIG[map.status];
                      return (
                        <div key={map.map_id} className="flex items-center justify-between p-3 bg-card rounded-lg">
                          <div>
                            <Text strong>{map.seed_keyword}</Text>
                            <div className="text-sm text-muted-foreground">{config.label}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-32">
                              <Progress
                                percent={map.progress_percentage}
                                size="small"
                                status="active"
                              />
                            </div>
                            <Popconfirm
                              title="Cancel this research?"
                              description="This will permanently delete the research session."
                              onConfirm={() => handleDeleteMap(map.map_id)}
                              okText="Delete"
                              cancelText="Keep"
                            >
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </Popconfirm>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchMaps()}
                    className="mt-3"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Status
                  </Button>
                </Card>
              )}

              {/* Completed Research Sessions */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Layers className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Your Research</h3>
                    {completedMaps.length > 0 && (
                      <Badge variant="secondary">{completedMaps.length}</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refetchMaps()}
                    disabled={mapsLoading}
                  >
                    <RefreshCw className={cn('h-4 w-4', mapsLoading && 'animate-spin')} />
                  </Button>
                </div>

                {mapsLoading && maps.length === 0 ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : completedMaps.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No completed research yet</p>
                    <p className="text-sm">Enter a seed keyword above to discover topics</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {completedMaps.map((map) => (
                      <div
                        key={map.map_id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:border-primary hover:bg-muted/30 cursor-pointer transition-all"
                        onClick={() => handleViewMap(map.map_id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Text strong className="text-base">{map.seed_keyword}</Text>
                            <Tag color="success">Ready</Tag>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Layers className="h-3.5 w-3.5" />
                              {map.total_clusters} clusters
                            </span>
                            <span className="flex items-center gap-1">
                              <Target className="h-3.5 w-3.5" />
                              {map.total_keywords} keywords
                            </span>
                            <span className="flex items-center gap-1">
                              <TrendingUp className="h-3.5 w-3.5" />
                              {map.total_volume?.toLocaleString() || 0} volume
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="default"
                            size="sm"
                            asChild
                          >
                            <Link to={`/content/planner/research/${map.map_id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Clusters
                            </Link>
                          </Button>
                          <Popconfirm
                            title="Delete this research?"
                            onConfirm={() => handleDeleteMap(map.map_id)}
                            okText="Delete"
                            cancelText="Cancel"
                          >
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </Popconfirm>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show failed maps separately */}
                {maps.filter(m => m.status === 'failed').length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Failed Research</h4>
                    <div className="space-y-2">
                      {maps.filter(m => m.status === 'failed').map((map) => (
                        <div
                          key={map.map_id}
                          className="flex items-center justify-between p-3 border border-destructive/20 bg-destructive/5 rounded-lg"
                        >
                          <div>
                            <Text>{map.seed_keyword}</Text>
                            <div className="text-xs text-destructive mt-0.5">
                              {(map as any).error_message || 'Processing failed'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRetryMap(map)}
                              className="text-teal border-teal/30 hover:bg-teal-light"
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Retry
                            </Button>
                            <Popconfirm
                              title="Delete this failed research?"
                              onConfirm={() => handleDeleteMap(map.map_id)}
                              okText="Delete"
                              cancelText="Cancel"
                            >
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </Popconfirm>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Flow hint */}
              <div className="text-center text-sm text-muted-foreground py-4">
                <div className="flex items-center justify-center gap-2">
                  <span>Research</span>
                  <ChevronRight className="h-4 w-4" />
                  <span>View Clusters</span>
                  <ChevronRight className="h-4 w-4" />
                  <span>Select Keywords</span>
                  <ChevronRight className="h-4 w-4" />
                  <span>Choose Template</span>
                  <ChevronRight className="h-4 w-4" />
                  <span className="font-medium text-foreground">Create Blogs</span>
                </div>
              </div>
            </div>
          )}
        </>
          </div>

          {/* Resize handles rendered LAST so they paint above the body content
              (and its scrollbar). Hidden in fullscreen. */}
          {!modalSize.isFullscreen && (
            <ModalResizeHandles onStart={modalSize.startResize} />
          )}
        </DialogContent>
      </Dialog>

      {/* Keyword dedup + WP-health warnings — rendered OUTSIDE <Dialog> so their
          portals overlay correctly and surface on the FIRST generate click.
          (Nested inside the create dialog they stayed invisible until the user
          closed and reopened the modal.) */}
      <AlertDialog open={showDedupConfirm} onOpenChange={setShowDedupConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Keyword Detected</AlertDialogTitle>
            <AlertDialogDescription>
              Content already exists for &ldquo;{parseKeywords(keywordsText)[0]}&rdquo;:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            {dedupConfirmData?.matches.map((match) => (
              <div key={match.id} className="flex items-center gap-2 text-sm">
                <span className="truncate flex-1">{match.title || match.keyword}</span>
                <Badge variant="outline">{match.status}</Badge>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => triggerWithHealthCheck()}>
              Create Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WPHealthWarningDialog {...wpHealthWarningProps} />

      {/* Bulk Generate -- rendered outside Dialog so it can overlay properly */}
      <TemplateSelectionModal
        open={bulkGenerateOpen}
        onCancel={() => setBulkGenerateOpen(false)}
        onConfirm={handleBulkGenerateSubmit}
        loading={isSubmitting}
      />

      <BatchScheduleDuplicateDialog
        open={!!duplicateScheduleResult}
        result={duplicateScheduleResult}
        loading={duplicateActionLoading || isSubmitting}
        onClose={() => setDuplicateScheduleResult(null)}
        onViewExisting={handleViewDuplicateContent}
        onDeleteAndRetry={handleDeleteDuplicatesAndRetry}
      />
    </>
  );
};

export default ContentCreationWizard;
